import { Refinement, z } from 'zod';
import { SignUpRequestCountryEnum } from '../openapi/yenta';
import { ALPHA_NUMERIC_REGEX_NO_SPACE, EMAIL_REGEX } from '../utils/RegexUtils';
import { PASSWORD_VALIDATIONS } from '../utils/zodValidations';

interface CreateSignupSchemaParams {
  usernameRefinement: Refinement<string>;
  emailRefinement: Refinement<string>;
}

export const OnboardingSignUpSchema = ({
  usernameRefinement,
  emailRefinement,
}: CreateSignupSchemaParams) =>
  z
    .object({
      firstName: z
        .string({ required_error: 'Please enter first name' })
        .trim()
        .min(1, { message: 'Please enter first name' }),
      lastName: z
        .string({
          required_error: 'Please enter last name',
        })
        .trim()
        .min(1, { message: 'Please enter last name' }),
      country: z.nativeEnum(SignUpRequestCountryEnum, {
        message: 'Please select country',
      }),
      username: z
        .string({
          required_error: 'Please enter username',
        })
        .trim()
        .min(2, { message: 'Username must be at least 2 characters' })
        .max(15, { message: 'Username must be fewer than 15 characters' })
        .refine((value) => ALPHA_NUMERIC_REGEX_NO_SPACE.test(value), {
          message: 'Username can only have letters and numbers',
        })
        .superRefine(async (value, ctx) => {
          const valid = await usernameRefinement(value, ctx);
          if (!valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Username is already taken',
            });
          }
        }),
      emailAddress: z
        .string({
          required_error: 'Please enter email address',
        })
        .trim()
        .min(1, { message: 'Please enter email address' })
        .refine((value) => EMAIL_REGEX.test(value), {
          message: 'Please enter valid email address',
        })
        .superRefine(async (value, ctx) => {
          const valid = await emailRefinement(value, ctx);
          if (!valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Email is already taken',
            });
          }
        }),
      password: PASSWORD_VALIDATIONS,
      confirmPassword: z
        .string({
          required_error: 'Please re-enter your password',
        })
        .trim()
        .min(1, { message: 'Please re-enter your password' }),
      consentedToTerms: z.boolean().refine((value) => value === true, {
        message: 'Please provide your consent to continue',
      }),
      consentedToCallMessage: z.boolean().refine((value) => value === true, {
        message: 'Please provide your consent to continue',
      }),
    })
    .superRefine(({ password, confirmPassword }, ctx) => {
      if (password !== confirmPassword) {
        ctx.addIssue({
          path: ['confirmPassword'],
          code: z.ZodIssueCode.custom,
          message: 'Passwords do not match',
        });
      }
    });
